<template>
  <div class="timeline-container">
    <p class="absolute text-sm text-[#666] font-medium">Encounter Notes</p>

    <div class="scroll-area">
      <!-- Left Arrow -->
      <button
        v-if="showLeftArrow"
        class="nav-arrow-position nav-arrow-left"
        @click="scrollLeft"
      >
      <div class="nav-arrow">     
        <svg width="6" height="10" viewBox="0 0 6 10" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M5 1L1 5L5 9" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      </button>

      <div
        ref="scrollContainer"
        class="scroll-wrapper"
        @scroll="updateArrowVisibility"
      >
        <div class="card-list">
          <div
            v-for="(note, index) in notes"
            :key="note.id || index"
            class="tab-card"
            :class="{ 
              'is-expanded': activeIndex === index,
              'is-current': isCurrentNote(note)
            }"
            @mouseenter="activateCard(note, index, $event)"
            @mouseleave="cancelPendingActivation"
          >
            <div class="tab-content">
              <div class="tab-header">
                <span class="date">{{ note.date }}</span>
                <span v-if="isNoteOwner(note)" class="author-dot"></span>
              </div>
              <span class="title">{{ note.title }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Arrow -->
      <button
        v-if="showRightArrow"
        class="nav-arrow-position nav-arrow-right"
        @click="scrollRight"
      >
      <div class="nav-arrow">     
        <svg width="6" height="10" viewBox="0 0 6 10" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M1 1L5 5L1 9" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      </button>
    </div>

    <Teleport to="body">
      <div
        v-if="activeNote"
        class="floating-card cursor-pointer"
        :style="cardStyle"
        @mouseleave="closeCard"
        @click="navigateToNote(activeNote)"
      >
        <div class="card-inner">
          <div class="card-header">
            <div class="header-row">
              <span class="date">{{ activeNote.date }}</span>
              <span v-if="isNoteOwner(activeNote)" class="author-tag">Created by You</span>
            </div>
            <span class="title">{{ activeNote.title }}</span>
          </div>

          <div class="expanded-details">
            <div class="note-content py-1">
              <template v-if="activeNote.content">
                <span>{{ getContentPreview(activeNote.content) }}</span>
              </template>
              <template v-else>N/A</template>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { computed, ref, nextTick, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useStore } from "vuex";
import { useActionCardsInlineText } from "../composables/useActionCardsInlineText";
import { generateNotePreview } from "../utils/actionCardUtils";

const router = useRouter();
const route = useRoute();
const store = useStore();
const { inlineTextMap, fetchAllInlineText } = useActionCardsInlineText();
fetchAllInlineText();

const getContentPreview = (contentHtml) => {
  if (!contentHtml) return '';
  return generateNotePreview(contentHtml, null, null, inlineTextMap.value);
};

const props = defineProps({
  notes: {
    type: Array,
    default: () => [],
  },
  patientId: {
    type: [String, Number],
    default: "",
  },
  currentNoteId: {
    type: [String, Number],
    default: null,
  },
});

// Get current user identifier for checking note ownership
const currentUserIdentifier = computed(() => {
  const userInfo = store.state.login?.userInfo;
  if (!userInfo?.clinic_id || !userInfo?.customer_id) return null;
  return `${userInfo.customer_id}_${userInfo.clinic_id}`;
});

// Check if a note was created by the current user
const isNoteOwner = (note) => {
  if (!currentUserIdentifier.value || !note?.created_by) return false;
  return note.created_by === currentUserIdentifier.value;
};

// Determine if a note is the currently selected/active note
const isCurrentNote = (note) => {
  const noteId = props.currentNoteId || route.params.noteId;
  if (!noteId || !note?.id) return false;
  return String(note.id) === String(noteId);
};

const emit = defineEmits(["beforeNavigate"]);

const activeNote = ref(null);
const activeIndex = ref(null); // Track ID/Index instead of object for safety
const position = ref({ top: 0, left: 0 });

let timer = null;

const activateCard = async (note, index, event) => {
  // Don't show floating card for current/selected note
  if (isCurrentNote(note)) return;
  
  if (timer) clearTimeout(timer);
  const target = event.currentTarget;

  timer = setTimeout(() => {
    const rect = target.getBoundingClientRect();

    position.value = {
      top: rect.top,
      left: rect.left,
    };

    activeIndex.value = index;
    activeNote.value = note;
    timer = null;
  }, 250);
};

const cancelPendingActivation = () => {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
};

const closeCard = () => {
  if (timer) clearTimeout(timer);
  activeIndex.value = null;
  activeNote.value = null;
};

const navigateToNote = (note) => {
  // Hide floating card immediately on click
  closeCard();
  
  if (!note?.id) return;
  
  // Use patient_id from note if available, otherwise use prop
  const patientId = note.patient_id || props.patientId;
  if (!patientId) {
    console.warn("Cannot navigate: patientId is not available");
    return;
  }
  
  const targetRoute = `/encounter-notes/${patientId}/${note.id}`;
  
  // Emit event to allow parent to save content/upload recordings before navigating
  emit("beforeNavigate", {
    targetRoute,
    patientId,
    noteId: note.id,
    navigate: () => router.push(targetRoute),
  });
};

const cardStyle = computed(() => {
  if (!activeNote.value) return {};
  return {
    top: `${position.value.top}px`,
    left: `${position.value.left}px`,
  };
});

const scrollContainer = ref(null);

// Arrow visibility state
const showLeftArrow = ref(false);
const showRightArrow = ref(false);

// Tab width + gap for scrolling one tab at a time
const TAB_WIDTH = 182;
const TAB_GAP = 10;
const SCROLL_AMOUNT = TAB_WIDTH + TAB_GAP;

// Update arrow visibility based on scroll position
const updateArrowVisibility = () => {
  const slider = scrollContainer.value;
  if (!slider) return;
  
  const { scrollLeft, scrollWidth, clientWidth } = slider;
  
  // Show left arrow if not at the start
  showLeftArrow.value = scrollLeft > 0;
  
  // Show right arrow if not at the end (with small tolerance for rounding)
  showRightArrow.value = scrollLeft < scrollWidth - clientWidth - 1;
};

// Scroll left by one tab
const scrollLeft = () => {
  const slider = scrollContainer.value;
  if (!slider) return;
  
  slider.scrollBy({
    left: -SCROLL_AMOUNT,
    behavior: 'smooth'
  });
};

// Scroll right by one tab
const scrollRight = () => {
  const slider = scrollContainer.value;
  if (!slider) return;
  
  slider.scrollBy({
    left: SCROLL_AMOUNT,
    behavior: 'smooth'
  });
};

// Scroll current tab to center of the viewport
const scrollCurrentTabToCenter = () => {
  nextTick(() => {
    const slider = scrollContainer.value;
    if (!slider) return;
    
    const currentTab = slider.querySelector('.tab-card.is-current');
    if (!currentTab) return;
    
    const containerWidth = slider.clientWidth;
    const tabLeft = currentTab.offsetLeft;
    const tabWidth = currentTab.offsetWidth;
    
    // Calculate scroll position to center the tab
    const scrollTarget = tabLeft - (containerWidth / 2) + (tabWidth / 2);
    
    // Clamp to valid scroll range (as center as possible)
    const maxScroll = slider.scrollWidth - containerWidth;
    const clampedScroll = Math.max(0, Math.min(scrollTarget, maxScroll));
    
    slider.scrollLeft = clampedScroll;
  });
};

// Flag to track if initial centering has been done
let initialCenteringDone = false;

// Watch for notes to be loaded (only centers once on initial load)
const stopWatcher = watch(
  () => props.notes,
  (newNotes) => {
    if (newNotes.length > 0 && !initialCenteringDone) {
      scrollCurrentTabToCenter();
      initialCenteringDone = true;
      // Defer stop to next tick so stopWatcher is assigned (immediate: true runs before watch returns)
      nextTick(() => {
        stopWatcher();
        updateArrowVisibility();
      });
    }
  },
  { immediate: true }
);

</script>

<style scoped>
.timeline-container {
  position: relative;
  width: 100%;
  min-width: 0;
}

.scroll-area {
  position: relative;
  padding-top: 32px;
}

.scroll-wrapper {
  width: 100%;
  overflow-x: auto;
  /* Hide scrollbar */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE/Edge */
}

/* Hide scrollbar for Chrome, Edge, Safari */
.scroll-wrapper::-webkit-scrollbar {
  display: none;
}

/* Navigation Arrows */
.nav-arrow-position {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  margin-top: 20px; /* Offset for padding-top */
  z-index: 10;
  padding:20px;
}

.nav-arrow {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #004879;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0px 3px 4px 0px rgba(0, 0, 0, 0.2);
  transition: background-color 0.15s ease;
}

.nav-arrow-left {
  left: -32px;
}

.nav-arrow-right {
  right: -32px;
}

.card-list {
  display: flex;
  gap: 10px;
  width: max-content;
  box-sizing: border-box;

}

/* Tab card - Default state */
.tab-card {
  width: 182px;
  background: #dde8ef;
  border-radius: 5px 5px 0 0;
  cursor: pointer;
  flex-shrink: 0;
  position: relative;
  box-shadow: 2px -3px 5px 0px rgba(74, 74, 74, 0.06);
  transition: width 0.25s cubic-bezier(0.25, 0.8, 0.5, 1),
              background-color 0s ease;
  will-change: width;
}

.tab-card:not(.is-current):hover {
  background: #fff;
  border-radius: 5px;
}

/* Current/Selected note - white background */
.tab-card.is-current {
  background: #fff;
  cursor: default;
}

.tab-card.is-current .title {
  font-weight: 500;
  letter-spacing: 0.1px;
  color: #004879;
}

.tab-card.is-expanded {
  width: 262px;
  background-color: #ebeef0;
}

.tab-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 6px 16px 10px 16px;
}

.tab-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.tab-card .date {
  font-family: 'IBM Plex Sans Hebrew', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.4px;
  color: #999;
}

.author-dot {
  width: 6px;
  height: 6px;
  background: #739cb6;
  border-radius: 50%;
  flex-shrink: 0;
}

.tab-card .title {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tab-card:not(.is-current):hover .title {
  font-weight: 500;
  letter-spacing: 0.1px;
  color: #004879;
}

/* --- Floating Overlay (Expanded Card) --- */
.floating-card {
  position: fixed;
  background: #ebeef0;
  border-radius: 5px;
  z-index: 9999;
  overflow: hidden;
  transform-origin: left top;
  animation: expandFromLeft 0.25s cubic-bezier(0.25, 0.8, 0.5, 1) forwards;
}

@keyframes expandFromLeft {
  from {
    width: 182px;
    opacity: 0.8;
  }
  to {
    width: 262px;
    opacity: 1;
  }
}

.card-inner {
  padding: 6px 16px 10px 16px;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.card-header {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.card-header .date {
  font-family: 'IBM Plex Sans Hebrew', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.4px;
  color: #999;
}

.author-tag {
  background: #739cb6;
  border-radius: 20px;
  padding: 0 10px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'IBM Plex Sans Hebrew', sans-serif;
  font-size: 10px;
  font-weight: 700;
  line-height: 12px;
  letter-spacing: 0.2px;
  color: #fff;
}

.card-header .title {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.expanded-details {
  background: #fff;
  border-radius: 5px;
  padding: 2px 10px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: #333;
  opacity: 0;
  animation: fadeIn 0.3s forwards 0.1s;
}

.note-content {
  height: 46px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

@keyframes fadeIn {
  to {
    opacity: 1;
  }
}
</style>
